<template>
  <div class="settings-design">
    <div class="card">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>Company details</div>
          <div class="card-subtitle">Shown on all owner reports and statements</div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="loading" class="loading-row">Loading company details…</div>
        <template v-else>
          <div class="logo-upload">
            <div class="logo-preview" :class="{ 'has-image': hasLogo }">
              <img v-if="hasLogo" :src="logoPreviewSrc" alt="Company logo"/>
              <svg v-else viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </div>
            <div>
              <div style="font-size:13px;font-weight:600;color:var(--text);margin-bottom:4px;">Company logo</div>
              <div style="font-size:12px;color:var(--text-muted);margin-bottom:10px;">PNG or SVG, at least 200×200px. Appears at the top of every owner PDF report.</div>
              <div style="display:flex;gap:8px;">
                <button class="btn btn-outline btn-sm" type="button" @click="triggerFileInput">
                  <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  {{ hasLogo ? 'Replace logo' : 'Upload logo' }}
                </button>
                <button v-if="hasLogo" class="btn btn-outline btn-sm" type="button" @click="removeLogo">
                  <svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>
                  Remove
                </button>
              </div>
              <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/svg+xml" style="display:none" @change="onFileSelected"/>
            </div>
          </div>
          <div class="field-grid cols-1" style="margin-bottom:14px;">
            <div class="field">
              <label class="field-label">Company name</label>
              <input type="text" v-model="profile.company_name" placeholder="e.g. Coastal Holiday Lets Ltd"/>
              <span class="field-hint">Printed on all owner reports and correspondence</span>
            </div>
          </div>
          <div class="field-grid">
            <div class="field"><label class="field-label">Company registration number</label><input type="text" v-model="profile.company_registration_number" placeholder="e.g. 12345678"/></div>
            <div class="field"><label class="field-label">VAT registration number</label><input type="text" v-model="profile.vat_registration_number" placeholder="e.g. GB 123 4567 89"/></div>
            <div class="field span2"><label class="field-label">Registered address</label><input type="text" v-model="profile.registered_address" placeholder="e.g. 1 Harbour Way, Poole, BH15 1AA"/></div>
          </div>

          <div class="section-divider">VAT</div>
          <div class="field-grid" style="margin-bottom:16px;">
            <div class="field">
              <label class="field-label">VAT rate</label>
              <div class="input-prefix"><span>%</span><input type="number" step="0.01" min="0" v-model.number="profile.vat_rate"/></div>
              <span class="field-hint">Applied to management fees and supplied services</span>
            </div>
          </div>
          <div class="section-divider">Sender identity</div>
          <div style="font-size:12px;color:var(--text-muted);margin-bottom:14px;line-height:1.6;padding:10px 12px;background:var(--bg);border-radius:8px;border:1px solid var(--border);">
            Emails leave from the Invoxis-authenticated address (<code>noreply@invoxis.app</code>), but recipients see your company name. Replies land in your inbox thanks to the Reply-To header.
          </div>
          <div class="field-grid" style="margin-bottom:16px;">
            <div class="field">
              <label class="field-label">Sender name</label>
              <input type="text" v-model="profile.sender_name" :placeholder="profile.company_name || 'e.g. Coastal Holiday Lets Ltd'"/>
              <span class="field-hint">Shown as the "From" name in every outgoing email. Blank = uses Company name.</span>
            </div>
            <div class="field">
              <label class="field-label">Reply-to email</label>
              <input type="text" v-model="profile.reply_to_email" placeholder="e.g. hello@yourcompany.co.uk"/>
              <span class="field-hint">Where owner replies are routed. Blank = your account email.</span>
            </div>
          </div>

          <div class="section-divider">Business model</div>
          <div class="radio-group horizontal" style="margin-bottom:16px;">
            <div class="radio-opt" :class="{ active: profile.business_model === 'agent' }" @click="profile.business_model = 'agent'">
              <div class="radio-dot-wrap"></div>
              <div class="radio-opt-text"><strong>Agent only</strong><span>VAT on management fees, consumables, and laundry. Cleaning and maintenance as disbursements.</span></div>
            </div>
            <div class="radio-opt" :class="{ active: profile.business_model === 'principal' }" @click="profile.business_model = 'principal'">
              <div class="radio-dot-wrap"></div>
              <div class="radio-opt-text"><strong>Principal</strong><span>VAT applies to all services including cleaning and maintenance.</span></div>
            </div>
          </div>
          <div class="btn-row-actions">
            <button class="btn btn-outline" type="button" @click="resetToSaved" :disabled="saving">Reset to saved</button>
            <button class="btn btn-primary" type="button" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save company details' }}</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CompanyTab',
  inject: {
    notify: { default: null },
  },
  data() {
    return {
      loading: true,
      saving: false,
      profile: this.emptyProfile(),
      savedProfile: this.emptyProfile(),
      logoFile: null,
      localLogoPreview: null,
      logoMarkedForRemoval: false,
    };
  },
  computed: {
    baseUrl() {
      if (window.location.pathname.startsWith('/dev')) {
        return window.location.origin + '/dev';
      }
      return window.location.origin;
    },
    hasLogo() {
      if (this.logoMarkedForRemoval) return false;
      return !!(this.localLogoPreview || this.profile.company_logo_url);
    },
    logoPreviewSrc() {
      return this.localLogoPreview || this.profile.company_logo_url || '';
    },
  },
  methods: {
    emptyProfile() {
      return {
        company_name: '',
        company_logo_url: null,
        company_registration_number: '',
        vat_registration_number: '',
        registered_address: '',
        vat_rate: 20,
        business_model: 'agent',
        sender_name: '',
        reply_to_email: '',
      };
    },
    async load() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${this.baseUrl}/api/users/organization-profile/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = r.data || {};
        const merged = { ...this.emptyProfile(), ...data };
        this.profile = merged;
        this.savedProfile = JSON.parse(JSON.stringify(merged));
        this.logoFile = null;
        this.localLogoPreview = null;
        this.logoMarkedForRemoval = false;
      } catch (e) {
        console.error('Error loading organization profile:', e);
      } finally {
        this.loading = false;
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.logoFile = file;
      this.logoMarkedForRemoval = false;
      const reader = new FileReader();
      reader.onload = (e) => { this.localLogoPreview = e.target.result; };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    removeLogo() {
      this.logoFile = null;
      this.localLogoPreview = null;
      this.logoMarkedForRemoval = true;
    },
    resetToSaved() {
      this.profile = JSON.parse(JSON.stringify(this.savedProfile));
      this.logoFile = null;
      this.localLogoPreview = null;
      this.logoMarkedForRemoval = false;
    },
    async save() {
      this.saving = true;
      try {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('company_name', this.profile.company_name || '');
        form.append('company_registration_number', this.profile.company_registration_number || '');
        form.append('vat_registration_number', this.profile.vat_registration_number || '');
        form.append('registered_address', this.profile.registered_address || '');
        form.append('vat_rate', this.profile.vat_rate);
        form.append('business_model', this.profile.business_model);
        form.append('sender_name', this.profile.sender_name || '');
        form.append('reply_to_email', this.profile.reply_to_email || '');
        if (this.logoFile) {
          form.append('company_logo', this.logoFile);
        } else if (this.logoMarkedForRemoval) {
          form.append('company_logo', '');
        }
        const r = await axios.patch(`${this.baseUrl}/api/users/organization-profile/`, form, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        const data = r.data || {};
        const merged = { ...this.emptyProfile(), ...data };
        this.profile = merged;
        this.savedProfile = JSON.parse(JSON.stringify(merged));
        this.logoFile = null;
        this.localLogoPreview = null;
        this.logoMarkedForRemoval = false;
        if (this.notify) {
          this.notify('success', 'Saved', 'Company details saved.');
        } else {
          alert('Company details saved.');
        }
      } catch (e) {
        console.error('Error saving organization profile:', e);
        const msg = e.response?.data?.detail || 'Failed to save company details.';
        if (this.notify) {
          this.notify('error', 'Save failed', msg);
        } else {
          alert(msg);
        }
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.settings-design {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --red: #d63031;
  --red-dim: rgba(214,48,49,0.08);
  --red-border: rgba(214,48,49,0.2);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.settings-design *, .settings-design *::before, .settings-design *::after { box-sizing: border-box; }

/* CARDS */
.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 18px; }
.card-header { padding: 16px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.card-title svg { width: 15px; height: 15px; fill: none; stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.card-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.card-body { padding: 20px 22px; }

.loading-row { color: var(--text-muted); font-size: 13px; padding: 16px 0; }

/* FIELDS */
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field-grid.cols-1 { grid-template-columns: 1fr; }
.field { display: flex; flex-direction: column; gap: 5px; }
.field.span2 { grid-column: 1/-1; }
label.field-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); }
input[type=text], input[type=number] {
  border: 1.5px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 13px; color: var(--text);
  font-family: 'Inter', sans-serif; background: var(--white); outline: none;
  transition: border-color 0.15s, box-shadow 0.15s; width: 100%;
}
input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(91,79,207,0.1); }
input::placeholder { color: var(--text-muted); }
.field-hint { font-size: 11px; color: var(--text-muted); line-height: 1.5; margin-top: 1px; }
.input-prefix { display: flex; align-items: center; }
.input-prefix span { background: var(--bg); border: 1.5px solid var(--border); border-right: none; border-radius: 8px 0 0 8px; padding: 9px 10px; font-size: 13px; color: var(--text-muted); white-space: nowrap; flex-shrink: 0; }
.input-prefix input { border-radius: 0 8px 8px 0; }

/* BUTTONS */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover:not(:disabled) { opacity: 0.88; }
.btn:active:not(:disabled) { transform: scale(0.98); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-sm { padding: 5px 11px; font-size: 12px; }
.btn svg { width: 13px; height: 13px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; }
.btn-row-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border); }

/* RADIO */
.radio-group { display: flex; flex-direction: column; gap: 8px; }
.radio-group.horizontal { flex-direction: row; }
.radio-opt { display: flex; align-items: flex-start; gap: 10px; padding: 11px 14px; border: 1.5px solid var(--border); border-radius: 9px; cursor: pointer; transition: border-color 0.15s, background 0.15s; flex: 1; }
.radio-opt:hover { border-color: var(--accent); background: var(--accent-dim); }
.radio-opt.active { border-color: var(--accent); background: var(--accent-dim); }
.radio-dot-wrap { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.radio-opt.active .radio-dot-wrap { border-color: var(--accent); background: var(--accent); }
.radio-opt.active .radio-dot-wrap::after { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #fff; }
.radio-opt-text strong { display: block; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.radio-opt.active .radio-opt-text strong { color: var(--accent); }
.radio-opt-text span { font-size: 11px; color: var(--text-muted); line-height: 1.4; }

/* SECTION DIVIDER */
.section-divider { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.09em; color: var(--text-muted); margin: 22px 0 14px; display: flex; align-items: center; gap: 10px; }
.section-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }

/* LOGO UPLOAD */
.logo-upload { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.logo-preview { width: 72px; height: 72px; border-radius: 10px; background: var(--bg); border: 2px dashed var(--border); display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden; }
.logo-preview.has-image { border-style: solid; padding: 4px; background: #fff; }
.logo-preview img { max-width: 100%; max-height: 100%; object-fit: contain; }
.logo-preview svg { width: 24px; height: 24px; fill: none; stroke: var(--text-muted); stroke-width: 1.5; }

@media (max-width: 768px) {
  .card { margin-bottom: 14px; }
  .card-header { padding: 14px 16px; flex-wrap: wrap; gap: 8px; }
  .card-body { padding: 16px 16px; }
  .card-title { font-size: 0.88rem; }
  .card-subtitle { font-size: 11.5px; }
  .field-grid { grid-template-columns: 1fr !important; gap: 12px; }
  .field.span2 { grid-column: 1; }
  .logo-upload { flex-wrap: wrap; gap: 12px; }
  .logo-preview { width: 60px; height: 60px; }
  .radio-group.horizontal { flex-direction: column; }
  .radio-opt { width: 100%; }
}
</style>
